'use client';

// biome-ignore lint/style/noRestrictedImports: Used to create our custom Link component
import { default as NextLink } from 'next/link';
import { type ComponentProps, forwardRef, useContext } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { GtmContext } from '@/app_lib/providers/GtmProvider';
import { type TrackEventParams, trackEvent } from '@/app_lib/services/Gtm/utils';
import { cn } from '@/app_lib/utils/utils';
import { trackOptimizelyEvent } from '@/main/lib/providers/OptimizelyProvider';

const contentLink = tv({
  variants: {
    variant: {
      primary:
        'text-blue-500 dark:text-blue-300 hover:text-primary-hover active:text-primary-pressed visited:text-primary-visited',
      secondary: 'text-red-500 dark:text-red-400 hover:underline',
    },
  },
});

const navLink = tv({
  base: 'font-medium',
  variants: {
    variant: {
      primary: 'hover:text-secondary active:text-secondary',
      secondary: 'hover:underline active:underline active:text-secondary',
    },
  },
});

type ContentLinkVariants = VariantProps<typeof contentLink>;
type NavLinkVariants = VariantProps<typeof navLink>;

export interface LinkProps
  extends ContentLinkVariants,
    NavLinkVariants,
    ComponentProps<typeof NextLink>,
    React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  className?: string;
  prefetch?: boolean;
  children: React.ReactNode;
  trackingData?: TrackEventParams;
  recipe?: 'content' | 'nav';
  optimizelyTrackingData?: { name: string; properties?: Record<string, string | number> };
}

/**
 * Use this for all internal navigation.
 *
 * @param recipe - the styling group to be used (defaults to no styling)
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      href,
      className,
      prefetch = true,
      children,
      variant,
      onClick,
      trackingData,
      recipe,
      'aria-label': ariaLabel,
      optimizelyTrackingData = { name: '', properties: {} },
      ...rest
    },
    ref,
  ) => {
    const style = recipe === 'content' ? contentLink : navLink;
    const { pageData } = useContext(GtmContext);

    return (
      <NextLink
        aria-label={ariaLabel || `Go to ${href}`}
        className={recipe ? cn(style({ variant }), className) : className}
        href={encodeURI(href)}
        onClick={(e) => {
          if (trackingData) {
            trackEvent({ ...pageData, ...trackingData.data }, trackingData.eventType);
          }
          onClick?.(e);
          if (optimizelyTrackingData) {
            trackOptimizelyEvent(optimizelyTrackingData.name, optimizelyTrackingData.properties ?? {});
          }
        }}
        prefetch={prefetch}
        ref={ref}
        {...rest}
      >
        {children}
      </NextLink>
    );
  },
);
